import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import ResizableHeader from "../../shared/ResizableHeader";
import TablePagination from "../../shared/TablePagination";
import useAutoAdvancePagination from "../../shared/useAutoAdvancePagination";
import ExportLoader from "../../shared/ExportLoader";
import useReportExport from "../../shared/useReportExport";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import SherComboBox from "../../../components/ComboBoxes/SherComboBox";
import GridDesign from "../../GridDesign/GridDesign";
import toExcelIcon from "../../../assets/images/ToExcel.png";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import io from "socket.io-client";

// Get default columns based on reportType to display headers when data is empty
const getDefaultColumns = (reportType) => {
  if (reportType === "Agent_List") {
    return [
      { key: "LedgerName", columnName: "LedgerName", header: "Agent Name", width: 220, align: "left", orderNo: 1, show: true },
      { key: "Address", columnName: "Address", header: "Address", width: 220, align: "left", orderNo: 2, show: true },
      { key: "Mobile", columnName: "Mobile", header: "Phone Number", width: 140, align: "left", orderNo: 3, show: true },
      { key: "Status", columnName: "Status", header: "Status", width: 90, align: "center", orderNo: 4, show: true },
    ];
  }
  // default Ledger List (LedgerName is now first)
  return [
    { key: "LedgerName", columnName: "LedgerName", header: "Ledger Name", width: 220, align: "left", orderNo: 1, show: true },
    { key: "ParentLedger", columnName: "ParentLedger", header: "Parent Ledger", width: 180, align: "left", orderNo: 2, show: true },
    { key: "LedCode", columnName: "LedCode", header: "LedCode", width: 90, align: "center", orderNo: 3, show: true },
    { key: "AccNo", columnName: "AccNo", header: "AccNo", width: 100, align: "left", orderNo: 4, show: true },
    { key: "Address", columnName: "Address", header: "Address", width: 220, align: "left", orderNo: 5, show: true },
    { key: "Agent", columnName: "Agent", header: "Agent", width: 130, align: "left", orderNo: 6, show: true },
    { key: "Mobile", columnName: "Mobile", header: "Phone Number", width: 140, align: "left", orderNo: 7, show: true },
  ];
};

function LedgerList({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();

  const parentRef = useRef(null);
  const lednameRef = useRef(null);
  const phoneNoRef = useRef(null);
  const agentRef = useRef(null);

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [employeeNames, setEmployeeNames] = useState([]);
  const [under, setUnder] = useState([]);
  const [Lednames, setLednames] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  const [LedgerListData, setLedgerListData] = useState({
    reportType: { value: "Ledger_List", label: "Ledger List" },
    parent: null,
    ledName: null,
    phoneNo: "",
    agent: null,
  });

  const reportType = LedgerListData.reportType ? LedgerListData.reportType.value : "Ledger_List";
  const formName = `Ledger List - ${reportType}`;

  const [gridColumns, setGridColumns] = useState(() => getDefaultColumns(reportType));
  const [showGridDesignModal, setShowGridDesignModal] = useState(false);

  // Initialize/Load cached column settings whenever report type changes
  useEffect(() => {
    const cached = localStorage.getItem(`grid_design_${formName}`);
    if (cached) {
      try {
        setGridColumns(JSON.parse(cached));
        return;
      } catch (e) {
        console.error("Error parsing cached grid settings:", e);
      }
    }
    setGridColumns(getDefaultColumns(reportType));
  }, [reportType, formName]);

  // Load grid settings from database and local cache matching SchemeReport.jsx structure
  useEffect(() => {
    const loadGridSettings = async () => {
      if (!dbCode || tableData.length === 0) return;

      const keys = Object.keys(tableData[0]);

      // Generate default columns dynamically based on API response
      const defaults = keys.map((key, idx) => {
        let label = key;
        let width = 150;
        let align = "left";

        if (key.toLowerCase() === "slno" || key.toLowerCase() === "rowid" || key.toLowerCase() === "nos") {
          label = "Sl No";
          width = 60;
          align = "center";
        } else if (key.toLowerCase().includes("ledname") || key.toLowerCase().includes("ledgername") || key.toLowerCase() === "ledger name") {
          label = "Ledger Name";
          width = 200;
        } else if (key.toLowerCase() === "lh_name" || key.toLowerCase() === "parent" || key.toLowerCase() === "parent ledger") {
          label = "Parent Ledger";
          width = 180;
        } else if (key.toLowerCase() === "mobile" || key.toLowerCase() === "phone" || key.toLowerCase() === "phone number") {
          label = "Phone Number";
          width = 140;
        } else if (key.toLowerCase() === "agent" || key.toLowerCase() === "salesman" || key.toLowerCase() === "name") {
          label = "Agent";
          width = 120;
        } else if (key.toLowerCase() === "status" || key.toLowerCase() === "active") {
          label = "Status";
          width = 90;
          align = "center";
        }

        return {
          key,
          columnName: key,
          header: label,
          width,
          align,
          orderNo: idx,
          show: true
        };
      });

      // Check cache first
      let cached = localStorage.getItem(`grid_design_${formName}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const cacheKeys = parsed.map(c => c.key);
          const keysMatch = keys.every(k => cacheKeys.includes(k));
          if (keysMatch) {
            setGridColumns(parsed);
            return;
          }
        } catch (e) {
          console.error("Error parsing cached grid settings:", e);
        }
      }

      // Fetch from API and merge/sort exactly like SchemeReport.jsx
      try {
        const response = await axios.get(
          `${apiBaseUrl}/main/griddesign/${dbCode}/${formName}`
        );
        const dbCols = response.data.recordset || response.data || [];
        if (dbCols.length > 0) {
          const merged = defaults.map((initCol) => {
            const dbMatch = dbCols.find(
              (c) =>
                String(c.ColName).toLowerCase() ===
                String(initCol.columnName).toLowerCase()
            );
            if (dbMatch) {
              return {
                ...initCol,
                width: dbMatch.Width !== null ? dbMatch.Width : initCol.width,
                orderNo: dbMatch.InNo !== null ? dbMatch.InNo : initCol.orderNo,
                header: dbMatch.Header || initCol.header,
                show: dbMatch.Vis === 1
              };
            }
            return initCol;
          });
          merged.sort((a, b) => a.orderNo - b.orderNo);
          localStorage.setItem(`grid_design_${formName}`, JSON.stringify(merged));
          setGridColumns(merged);
        } else {
          setGridColumns(defaults);
        }
      } catch (error) {
        console.error("Error loading grid settings from database:", error);
        setGridColumns(defaults);
      }
    };

    loadGridSettings();
  }, [tableData, dbCode, apiBaseUrl, formName]);

  const visibleColumns = useMemo(
    () => gridColumns.filter((col) => col.show),
    [gridColumns]
  );

  const gridTemplateColumns = useMemo(
    () => {
      return `60px ` + visibleColumns.map((col) => `${col.width}px`).join(" ");
    },
    [visibleColumns]
  );

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const params = {
        reportType: LedgerListData.reportType ? [LedgerListData.reportType.value, LedgerListData.reportType.label] : ["Ledger_List", "Ledger List"],
        parent: LedgerListData.parent ? [LedgerListData.parent.value, LedgerListData.parent.label] : "",
        ledName: LedgerListData.ledName ? [LedgerListData.ledName.value, LedgerListData.ledName.label] : "",
        phoneNo: LedgerListData.phoneNo || "",
        agent: LedgerListData.agent ? [LedgerListData.agent.value, LedgerListData.agent.label] : "",
      };

      const response = await axios.get(`${apiBaseUrl}/main/LedList/${dbCode}`, {
        params,
        paramsSerializer: (params) => {
          return Object.entries(params)
            .map(([key, val]) => {
              if (Array.isArray(val)) {
                return val
                  .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
                  .join("&");
              }
              return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
            })
            .join("&");
        },
      });
      const data = response.data || [];
      setTableData(data);
      
      // Shrink / collapse sidebar if data is present
      if (data.length > 0) {
        setIsSidebarExpanded(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setLedgerListData({
      reportType: { value: "Ledger_List", label: "Ledger List" },
      parent: null,
      ledName: null,
      phoneNo: "",
      agent: null,
    });
    setTableData([]);
    setIsSidebarExpanded(true);
  };

  const handleRowClick = (index) => {
    setSelectedRowIndex(index);
  };

  const fetchparent = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/spledgerheads/${dbCode}`,
      );
      const parDetails = response.data || [];
      const parentOptions = parDetails.map((item) => ({ value: item.lh_id, label: item.lh_name }));
      setUnder(parentOptions);
    } catch (error) {
      console.error("Error fetching names Values:", error.message);
    }
  };

  const fetchLednamesNames = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/spledgernames/${dbCode}`,
      );
      const ledDetails = response.data || [];
      const ledOptions = ledDetails.map((item) => ({ value: item.Ledcode, label: item.LedName }));
      setLednames(ledOptions);
    } catch (error) {
      console.error("Error fetching names Values:", error.message);
    }
  };

  useEffect(() => {
    const socket = io(apiBaseUrl);
    socket.on("dataUpdated", () => {
      fetchLednamesNames();
    });

    return () => {
      socket.disconnect();
    };
  }, [apiBaseUrl]);

  // Client-side quick search filter
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const query = searchQuery.toLowerCase().trim();
    return tableData.filter((row) =>
      visibleColumns.some((col) => {
        const val = String(row[col.key] ?? "");
        return val.toLowerCase().includes(query);
      })
    );
  }, [tableData, searchQuery, visibleColumns]);

  const {
    currentPage,
    pageSize,
    pageSizeOptions,
    totalPages,
    totalRows,
    displayedRows,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleWheel,
    handleKeyDown,
    handlePageChange,
    handlePageSizeChange,
    autoAdvance,
    toggleAutoAdvance,
    isFlipping,
  } = useAutoAdvancePagination({
    items: filteredRows,
    initialPageSize: 100,
    pageSizeOptions: [50, 100, 250, 500],
    initialAutoAdvance: true,
  });

  const { exportingText, runExport } = useReportExport();

  const exportToExcel = () => {
    if (filteredRows.length === 0) {
      alert("No data available to export!");
      return;
    }
    runExport("Generating Excel Spreadsheet...", () => {
      const worksheet = XLSX.utils.json_to_sheet(filteredRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "LedgerList");
      XLSX.writeFile(workbook, "LedgerList.xlsx");
    });
  };

  const fetchEmployeeNames = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/SchReg_empname/${dbCode}`,
      );
      const empNames = (response.data || []).map((item) => ({ value: item.Auto, label: item.name }));
      setEmployeeNames(empNames);
    } catch (error) {
      console.error("Error fetching employee names:", error.message);
    }
  };

  // Stable column resize handler (shared ResizableHeader imported at module scope)
  const handleColumnResize = useCallback((key, newWidth, name) => {
    setGridColumns((prev) => {
      const updated = prev.map((c) => (c.key === key ? { ...c, width: newWidth } : c));
      localStorage.setItem(`grid_design_${name}`, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Standard input class for all comboboxes to replace standard dark borders
  const inputClass = "w-full px-2 text-xs border border-slate-200 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400";

  return (
    <div
      className="bg-[#d2e4fc] w-full h-full rounded-none shadow-2xl flex flex-col items-center absolute left-0 top-0 p-[1%] font-Inter"
      style={{ zIndex }}
    >
      {/* Main Container - Split View Gap */}
      <div className="w-full flex-1 flex gap-3 overflow-hidden relative">
        
        {/* Left Side: Filter Panel (Separate White Rounded Card) */}
        <div
          className={`bg-white border border-[#b8cce0] rounded-[10px] shadow-sm h-full transition-all duration-300 flex flex-col shrink-0 overflow-y-auto ${
            isSidebarExpanded ? "w-[300px] px-4 py-4" : "w-0 overflow-hidden border-none shadow-none"
          }`}
        >
          {isSidebarExpanded && (
            <div className="flex flex-col gap-2.5 font-Inter">
              
              {/* Title: Report Parameters */}
              <div className="flex items-center gap-2 pb-1.5 border-b border-gray-200">
                <img src={headeLogo} alt="Logo" className="h-5 w-auto object-contain" />
                <span className="font-bold text-slate-800 text-sm font-Inter">Report Parameters</span>
              </div>

              {/* REPORT SETTINGS Section */}
              <div className="flex flex-col gap-2">
                <span className="text-[10px] font-bold text-slate-400 font-Inter uppercase tracking-wider">REPORT SETTINGS</span>

                {/* Report Type */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-xs text-slate-700 font-bold font-Inter">Report Type:</label>
                  <SherComboBox
                    options={[
                      { value: "Ledger_List", label: "Ledger List" },
                      { value: "Agent_List", label: "Agent List" },
                    ]}
                    value={LedgerListData.reportType?.value || ""}
                    onSelect={(opt) =>
                      setLedgerListData({
                        ...LedgerListData,
                        reportType: opt,
                      })
                    }
                    className="w-full font-Inter"
                    inputClassName={inputClass}
                    inputHeight="28px"
                  />
                </div>

                {/* Agent */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-xs text-slate-700 font-bold font-Inter">Agent:</label>
                  <SherComboBox
                    comboRef={agentRef}
                    value={LedgerListData.agent?.value || ""}
                    onFocus={fetchEmployeeNames}
                    options={employeeNames}
                    onSelect={(opt) =>
                      setLedgerListData({
                        ...LedgerListData,
                        agent: opt,
                      })
                    }
                    placeholder="Search Agent..."
                    className="w-full font-Inter"
                    inputClassName={inputClass}
                    inputHeight="28px"
                  />
                </div>

                {/* Parent Ledger */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-xs text-slate-700 font-bold font-Inter">Parent Ledger:</label>
                  <SherComboBox
                    comboRef={parentRef}
                    value={LedgerListData.parent?.value || ""}
                    options={under}
                    onFocus={fetchparent}
                    onSelect={(opt) =>
                      setLedgerListData({
                        ...LedgerListData,
                        parent: opt,
                      })
                    }
                    placeholder="Select Parent Ledger..."
                    className="w-full font-Inter"
                    inputClassName={inputClass}
                    inputHeight="28px"
                  />
                </div>
              </div>

              {/* LEDGER DETAILS Section */}
              <div className="flex flex-col gap-2 pt-2 border-t border-slate-200">
                <span className="text-[10px] font-bold text-slate-400 font-Inter uppercase tracking-wider">LEDGER DETAILS</span>

                {/* Ledger Name */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-xs text-slate-700 font-bold font-Inter">Ledger Name:</label>
                  <SherComboBox
                    comboRef={lednameRef}
                    options={Lednames}
                    value={LedgerListData.ledName?.value || ""}
                    onSelect={(opt) =>
                      setLedgerListData({
                        ...LedgerListData,
                        ledName: opt,
                      })
                    }
                    onFocus={fetchLednamesNames}
                    placeholder="Search name..."
                    className="w-full font-Inter"
                    inputClassName={inputClass}
                    inputHeight="28px"
                  />
                </div>

                {/* Phone Number */}
                <div className="flex flex-col gap-0.5">
                  <label className="text-xs text-slate-700 font-bold font-Inter">Phone Number:</label>
                  <input
                    ref={phoneNoRef}
                    type="text"
                    value={LedgerListData.phoneNo || ""}
                    onChange={(e) =>
                      setLedgerListData({
                        ...LedgerListData,
                        phoneNo: e.target.value,
                      })
                    }
                    placeholder="Enter Phone Number..."
                    className={inputClass}
                    style={{ height: "28px" }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-1.5 mt-2">
                <button
                  onClick={fetchData}
                  className="w-full bg-[#088395] hover:bg-[#066f7f] text-white font-bold py-1.5 rounded shadow-sm text-xs font-Inter border-none cursor-pointer flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  VIEW REPORT
                </button>

                <button
                  onClick={exportToExcel}
                  className="w-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold py-1.5 rounded shadow-sm text-xs font-Inter cursor-pointer flex items-center justify-center gap-2"
                >
                  <img className="h-4 w-auto" src={toExcelIcon} alt="Excel" />
                  EXPORT EXCEL
                </button>
              </div>

            </div>
          )}
        </div>

        {/* Vertical Tab Toggle Button */}
        <div
          onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
          className={`w-[20px] h-[40px] bg-[#1e73be] hover:bg-blue-700 text-white flex items-center justify-center cursor-pointer select-none font-bold shadow-md absolute top-1/2 -translate-y-1/2 z-20 rounded-r transition-all duration-300 ${
            isSidebarExpanded ? "left-[300px]" : "left-0"
          }`}
        >
          {isSidebarExpanded ? "«" : "»"}
        </div>

        {/* Right Side: Report Grid & Table (Separate White Rounded Card) */}
        <div className="flex-1 h-full bg-white border border-[#b8cce0] rounded-[10px] shadow-sm flex flex-col p-4 overflow-hidden">
          
          {/* Header Row on top of report */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 mb-3 font-Inter">
            <div className="flex items-center gap-3">
              <span className="font-bold text-slate-800 text-lg">Ledger List</span>
              <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded-full text-[11px] font-semibold">
                Total Records: {filteredRows.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={exportToExcel}
                className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
              >
                <img className="h-3.5 w-auto" src={toExcelIcon} alt="Excel" />
                Excel
              </button>

              <button
                onClick={fetchData}
                className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3m0 0l3 3m-3-3v12" />
                </svg>
                Refresh
              </button>

              <button
                onClick={() => setShowGridDesignModal(true)}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 p-1.5 rounded text-xs font-bold border-none cursor-pointer flex items-center justify-center"
                title="Design Columns"
              >
                ⋮
              </button>
              
              <button
                onClick={onClose}
                className="bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-700 p-1.5 rounded text-xs font-bold border-none cursor-pointer flex items-center justify-center ml-1"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Quick Search bar above table */}
          <div className="w-full flex justify-end pb-3">
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Search across all columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 text-xs border border-slate-300 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400 h-[28px]"
              />
              <svg
                className="absolute left-2.5 top-2 h-3.5 w-3.5 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-600 text-xs font-semibold px-1"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Scrollable Data Table Container - Exact Match to SchemeReport.jsx layout */}
          <div
            ref={tableContainerRef}
            className="w-full flex-1 min-h-0 overflow-auto border border-slate-300 rounded text-slate-800 text-[11px] font-semibold font-Inter outline-none focus:ring-1 focus:ring-blue-400"
            tabIndex={0}
            onScroll={handleScroll}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
          >
            {isLoading ? (
              <div className="w-full h-full flex items-center justify-center bg-white/70">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="font-Inter text-xs text-blue-900 font-semibold">Loading Ledger Data...</span>
                </div>
              </div>
            ) : (
              <div className="min-w-max flex flex-col min-h-full">
                
                {/* Header Row utilizing CSS Grid with border dividers */}
                <div className="sticky top-0 z-10 bg-[#1e73be] border-b border-slate-300 shrink-0">
                  <div className="grid gap-0 h-8" style={{ gridTemplateColumns }}>
                    <div className="font-Inter text-[11px] font-bold text-white border-r border-slate-300 px-3 flex items-center justify-center">
                      Sl No
                    </div>
                    {visibleColumns.map((col) => (
                      <ResizableHeader key={col.key} col={col} formName={formName} onResize={handleColumnResize}>
                        <div className="font-Inter text-[11px] font-bold text-white border-r border-slate-300 px-3 flex items-center justify-center w-full h-full truncate">
                          {col.header}
                        </div>
                      </ResizableHeader>
                    ))}
                  </div>
                </div>

                {/* Data Rows utilizing CSS Grid with border-r and border-b dividers - Expandable to fill height */}
                <div className="bg-white flex-1">
                  {filteredRows.length === 0 ? (
                    <div className="font-Inter text-xs text-gray-400 text-center py-12">
                      No records to display. Select filters and click Show.
                    </div>
                  ) : (
                    displayedRows.map((row, index) => {
                      const isHighlighted = index === selectedIndex || index === selectedRowIndex;
                      return (
                        <div
                          key={index}
                          data-row-index={index}
                          onClick={() => { handleRowClick(index); setSelectedIndex(index); }}
                          className={`grid gap-0 h-8 border-b border-slate-300 hover:bg-blue-50/40 cursor-pointer transition-colors duration-150 ${
                            isHighlighted
                              ? "bg-blue-100 font-bold text-blue-900 ring-1 ring-inset ring-blue-500"
                              : index % 2 === 0
                              ? "bg-white"
                              : "bg-[#f2f7ff]"
                          }`}
                          style={{ gridTemplateColumns }}
                        >
                          {/* Sl No */}
                          <div className="font-Inter text-[11px] border-r border-slate-300 px-3 text-slate-700 flex items-center justify-center">
                            {index + 1}
                          </div>

                          {/* Dynamic Columns */}
                          {visibleColumns.map((col) => {
                            const val = String(row[col.key] ?? "");
                            const isBlueLinkCol =
                              col.key.toLowerCase().includes("name") ||
                              col.key.toLowerCase().includes("agent");

                            // Custom format status active/inactive
                            if (col.key.toLowerCase() === "status" || col.key.toLowerCase() === "active") {
                              const isActive = val.toLowerCase() === "active" || val === "1" || val.toLowerCase() === "true";
                              return (
                                <div
                                  key={col.key}
                                  className="font-Inter text-[11px] border-r border-slate-300 px-3 shrink-0 flex items-center justify-center"
                                >
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                                      isActive
                                        ? "bg-green-100 text-green-700 border border-green-200"
                                        : "bg-red-100 text-red-700 border border-red-200"
                                    }`}
                                  >
                                    {isActive ? "Active" : "Inactive"}
                                  </span>
                                </div>
                              );
                            }

                            return (
                              <div
                                key={col.key}
                                className={`font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center truncate ${
                                  isBlueLinkCol
                                    ? "text-[#1e73be] hover:underline font-medium cursor-pointer"
                                    : "text-slate-700"
                                } ${
                                  col.align === "center"
                                    ? "justify-center text-center"
                                    : col.align === "right"
                                    ? "justify-end text-right"
                                    : ""
                                }`}
                              >
                                {val === "null" || val === "-" ? "" : val}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer Totals Row utilizing CSS Grid - Pinned bottom of scrollable content */}
                {filteredRows.length > 0 && (
                  <div
                    className="grid gap-0 h-8 bg-[#dbeafe] text-blue-900 font-bold sticky bottom-0 z-10 border-t border-slate-300 shrink-0"
                    style={{ gridTemplateColumns }}
                  >
                    <div className="font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center justify-center">
                      Total
                    </div>
                    {visibleColumns.map((col, i) => (
                      <div
                        key={col.key}
                        className="font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center truncate"
                      >
                        {i === 0 ? `${filteredRows.length} Records` : ""}
                      </div>
                    ))}
                  </div>
                )}

              </div>
            )}
          </div>

          <TablePagination
            currentPage={currentPage}
            totalRows={totalRows}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            autoAdvance={autoAdvance}
            onToggleAutoAdvance={toggleAutoAdvance}
            isFlipping={isFlipping}
          />

        </div>

      </div>

      {/* Grid Design Modal */}
      {showGridDesignModal && (
        <GridDesign
          onClose={() => setShowGridDesignModal(false)}
          onSave={(updated) => {
            setGridColumns(updated);
            setShowGridDesignModal(false);
          }}
          formName={formName}
          initialColumns={gridColumns}
          zIndex={zIndex + 10}
        />
      )}

      {/* Global Export Loader for Heavy Operations */}
      <ExportLoader message={exportingText} />
    </div>
  );
}

export default LedgerList;
